import {
  Box,
  Button,
  Typography,
  Chip
} from '@mui/material';
import { Grid } from '@mui/material';
import DisplayCard from '../ui/Card/DisplayCard';
import { useState } from 'react';
import ConfirmDialog from '../ui/Modal/ConfirmDialog';
import EventFormModal from './EventFormModal';
import { MapPin, Mic, Plus } from 'lucide-react';
import { undoEventDelete, deleteHotelSection } from '../../api/hotelinfoApi';
import { useUndoableDelete } from '../../hooks/useUndoableDelete';
import UndoSnackbar from '../ui/Feedback/UndoSnackbar';
import { hasFeature } from '../../permissions';
import PermissionDialog from '../ui/Modal/PermissionDialog';


const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:7071';

const saveEvent = async (eventData) => {
  const payload = {
    ...eventData,
    startDate: eventData.startDate || new Date().toISOString(),
    endDate: eventData.endDate || eventData.startDate || new Date().toISOString(),
    availableHours: eventData.availableHours || { from: '', to: '' },
    description: eventData.description || '',
    price: eventData.price || 0,
    location: eventData.location || '',
    speakerOrHost: eventData.speakerOrHost || '',
    bookingRequired: eventData.bookingRequired || false,
    isFeatured: eventData.isFeatured || false,
    tags: Array.isArray(eventData.tags) ? [...eventData.tags] : [],
    imageUrls: Array.isArray(eventData.imageUrls) ? [...eventData.imageUrls] : [],
    priority: eventData.priority || 0
  };

  const res = await fetch(`${API_BASE}/api/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });

  if (!res.ok) {
    throw new Error('Failed to save event');
  }
};

export default function EventSection({
  entries,
  setEntries,
  form,
  setForm,
  open,
  setOpen,
  handleEdit,
  handleDelete,
  propertyId,
  fetchEntries,
  allowedFeatures
}) {
  // New states for undoable delete
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deletedCardId, setDeletedCardId] = useState(null);

  const [permOpen, setPermOpen] = useState(false);
  const [permMsg, setPermMsg] = useState('');
  const showPerm = (msg) => { setPermMsg(msg); setPermOpen(true); };

  const getLocalAF = () => {
    try { return JSON.parse(localStorage.getItem('allowedFeatures') || '{}'); }
    catch (e) { console.warn('[EventSection] Failed to parse allowedFeatures from localStorage:', e); return {}; }
  };

  const AF = allowedFeatures || getLocalAF();
  const canAdd = !!(AF?.events?.addEvent ?? hasFeature('events.addEvent'));
  const canEdit = !!(AF?.events?.editEvent ?? hasFeature('events.editEvent'));
  const canDelete = !!(AF?.events?.deleteEvent ?? hasFeature('events.deleteEvent'));

  if (process.env.NODE_ENV !== 'production') {
    console.log('[EventSection] permissions ->', { canAdd, canEdit, canDelete, events: AF?.events });
  }

  const {
    undoItem,
    showUndo,
    triggerDelete,
    triggerUndo,
    remainingTime
  } = useUndoableDelete({
    onDeleteApi: (item) => deleteHotelSection('events', item._id, propertyId),
    onUndoApi: (item) => undoEventDelete(item._id, propertyId),
  });

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2} pr={3}>
        <Button
          variant="contained"
          aria-disabled={!canAdd}
          onClick={() => {
            if (!canAdd) { showPerm('You do not have permission to add events.'); return; }
            setForm({
              title: '',
              description: '',
              startDate: null,
              endDate: null,
              availableHours: { from: '', to: '' },
              price: '',
              location: '',
              speakerOrHost: '',
              bookingRequired: false,
              tags: [],
              imageUrls: [],
              _id: null
            });
            setOpen(true);
          }}
          startIcon={<Plus size={16} />}
          sx={{
            backgroundColor: canAdd ? undefined : 'lightgray',
            color: canAdd ? undefined : 'white',
            opacity: canAdd ? 1 : 0.4,
            '&:hover': { backgroundColor: canAdd ? undefined : 'lightgray' },
            pointerEvents: 'auto',
            cursor: canAdd ? 'pointer' : 'not-allowed'
          }}
        >
          Add Event
        </Button>
      </Box>
      <EventFormModal
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={(formData) => {
          if (formData._id && !canEdit) { showPerm('You do not have permission to edit events.'); return; }
          if (!formData._id && !canAdd) { showPerm('You do not have permission to add events.'); return; }

          const method = formData._id ? 'PUT' : 'POST';
          const url = formData._id
            ? `${API_BASE}/api/events/${formData._id}`
            : `${API_BASE}/api/events`;

          fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...formData, propertyId })
          }).then(res => {
            if (res.ok) {
              setOpen(false);
              setForm({});
              fetchEntries();
            } else {
              console.error('Failed to save event');
            }
          });
        }}
        initialData={form}
      />
      <Grid container spacing={2} columns={12} justifyContent="center" maxWidth="xl" sx={{ mt: 2 }}>
        {entries.map((entry, index) => (
          <Grid
            key={entry._id || index}
            sx={{
              gridColumn: {
                xs: 'span 12',
                sm: 'span 6',
                md: 'span 4',
                lg: 'span 3',
                xl: 'span 2',
              }
            }}
          >
            <DisplayCard
              entry={entry}
              onEdit={() => {
                if (!canEdit) { showPerm('You do not have permission to edit events.'); return; }
                handleEdit(entry);
              }}
              onDelete={() => {
                if (!canDelete) { showPerm('You do not have permission to delete events.'); return; }
                setDeletingId(entry._id);
                setConfirmOpen(true);
              }}
              onDuplicate={async () => {
                if (!canAdd) { showPerm('You do not have permission to add events.'); return; }
                const { _id, createdAt, updatedAt, __v, ...rest } = entry;
                try {
                  await saveEvent({ ...rest, title: `${entry.title} (Copy)`, propertyId });
                  fetchEntries();
                } catch (err) {
                  console.error(err.message);
                }
              }}
              editAllowed={canEdit}
              deleteAllowed={canDelete}
              duplicateAllowed={canAdd}
              isDeleted={entry._id === deletedCardId}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {entry.title}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {entry.description}
              </Typography>

              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Date: {new Date(entry.startDate).toLocaleDateString()}
              </Typography>

              {entry.availableHours?.from && entry.availableHours?.to && (() => {
                const [startHour, startMin] = entry.availableHours.from.split(':').map(Number);
                const [endHour, endMin] = entry.availableHours.to.split(':').map(Number);
                const start = startHour + startMin / 60;
                const end = endHour + endMin / 60;
                const duration = end > start ? end - start : (24 - start + end);
                return (
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Duration: {duration % 1 === 0 ? duration : duration.toFixed(1)} hour{duration > 1 ? 's' : ''}
                  </Typography>
                );
              })()}

              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Price: USD {entry.price}
              </Typography>

              {entry.location && (
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                  <MapPin size={16} />
                  <Typography variant="body2" color="text.secondary">
                    {entry.location}
                  </Typography>
                </Box>
              )}

              {entry.speakerOrHost && (
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                  <Mic size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Host: {entry.speakerOrHost}
                  </Typography>
                </Box>
              )}

              {entry.availableHours?.from && entry.availableHours?.to && (
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Time: {entry.availableHours.from} – {entry.availableHours.to}
                </Typography>
              )}

              {entry.endDate && entry.startDate !== entry.endDate && (
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  End: {new Date(entry.endDate).toLocaleDateString()}
                </Typography>
              )}

              {entry.bookingRequired && (
                <Box mt={0.1}>
                  <Typography variant="caption" color="error">
                    Booking Required
                  </Typography>
                </Box>
              )}

              {Array.isArray(entry.tags) && entry.tags.filter(tag => tag).length > 0 && (
                <Box mt={0.5} display="flex" flexWrap="wrap" gap={0.3} justifyContent="center">
                  {entry.tags.filter(tag => tag).map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              )}
            </DisplayCard>
          </Grid>
        ))}
      </Grid>
      <PermissionDialog open={permOpen} onClose={() => setPermOpen(false)} message={permMsg} />
      <ConfirmDialog
        open={confirmOpen}
        title="Delete Event"
        content="Are you sure you want to delete this event?"
        onCancel={() => {
          setConfirmOpen(false);
          setDeletingId(null);
        }}
        onConfirm={() => {
          if (!canDelete) { console.warn('[EventSection] Confirm delete blocked by permission'); return; }
          const itemToDelete = entries.find(e => e._id === deletingId);
          setDeletedCardId(deletingId);
          triggerDelete(itemToDelete);
          setConfirmOpen(false);
          setDeletingId(null);
        }}
      />
      <UndoSnackbar
        show={showUndo}
        label="Event deleted"
        onUndo={() => {
          setDeletedCardId(null);
          triggerUndo();
          fetchEntries();
        }}
        remainingTime={remainingTime}
      />
    </>
  );
}